import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'; // React State Management
import { useParams } from 'react-router';
import { ToastContainer, OverlayTrigger, Tooltip, Container, Table, Dropdown, Row, Col, Tabs, Tab, Accordion, Alert, Button, ProgressBar, Toast } from 'react-bootstrap';
import { getJob, getJobFiles } from '../apis/jobApi';
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ModFileTable } from '../components/Files/ModFileTable';
import { JobHistory } from '../components/Jobs/History/JobHistory';
import { JobProgress } from '../components/Jobs/JobProgress';
import { JobTasks } from '../components/Jobs/JobTasks';
import { JobAccessModal } from '../components/Jobs/JobAccessModal';
import { JobActions } from '../components/Jobs/JobActions';
import { dateTimeFormatter, durationStateFormatter, durationTaskFormatter } from '../library/formatters';
import { updateJobState } from '../apis/jobApi';
import { ThreeDotsVertical, ExclamationTriangleFill, ArrowClockwise } from 'react-bootstrap-icons';
import { usePermissionContext } from '../auth/AuthContext';
import { useDownloadFileProgress } from '../context/DownloadFileContext';
import { XCircle, ExclamationCircle } from 'react-bootstrap-icons';

export const JobPage = () => 
{
    const [openAccess, setOpenAccess] = useState(false);
    const [activeTab, setActiveTab] = useState('tasks');
    const [errorMessage, setErrorMessage] = useState("");
    const [tabErrorMessage, setTabErrorMessage] = useState({output:null, input:null, log:null});

    const {hasRole} = usePermissionContext();
    const isAdmin = hasRole('Admin');
    const queryClient = useQueryClient();
    const {downloads, clearDownloads, completedDownloads, removeDownload, cancelDownload} = useDownloadFileProgress();
    const { id } = useParams();
    const columns = { sm: 1, md: 1, lg: 1};

    const checkJobRunning = (data) => {
        if (!data) return false;
        const jobState = data;
        return !(jobState === "Draft" || jobState === "Completed" || jobState === "Failed" || jobState === "Purged" || jobState === "Paused");
    }


    const { isLoading, isError, error, data, refetch } = useQuery({
        queryKey: [`job`, id],
        queryFn: async () => {
            return await getJob(id);
        },
        keepPreviousData : true,         
        refetchInterval: (query) => {
            if (query?.state?.status === "error") return false;
            
            const state = query?.state?.data?.state;
            if(state === "Draft" || state === undefined || state === null)
                return 10000
            return (checkJobRunning(state) ? 10000 : false)}, // Only refresh if the job is not complete or failed or purged or paused
    });

    const isJobRunning = useMemo(() => 
        {
            return checkJobRunning(data?.state)
        }, [data]);

    const getFiles = async(params, fileType) => {

        const response = await getJobFiles(id, fileType, params.filterModel, params.sortModel, params.startRow, params.endRow-params.startRow)
        .then(response => {
            setTabErrorMessage(prev => ({...prev, [fileType.toLowerCase()]: null}));
            return {total: response.total, data: response.data.map((fileRow => fileRow))};
        })
        .catch(error => {
            if (error.status === 403)
            {
                setTabErrorMessage(prev => ({...prev, [fileType.toLowerCase()]: `You do not have permission to view ${fileType} files. Please contact your administrator.`}));
            }
            else
            {
                setTabErrorMessage(prev => ({...prev, [fileType.toLowerCase()]: error.message}));
            }
            return {total:0, data: []};
        });

        let lastRow = response.total;
        params.successCallback(response.data, lastRow);
    }

    const getInputFiles = async (params) => {   
        getFiles(params, 'Input');
    }

    const getOutputFiles = async (params) => {
        getFiles(params, 'Output');
    }


    const getLogFiles = async (params) => {
        getFiles(params, 'Log');
    }

    const purgeJob = async() =>{
        try
        {
            const response = await updateJobState(id, "purge");
        }
        catch(error)
        {
            if(error.status === 403)
            {
                setErrorMessage('You do not have permission to perform this action. Please contact your administrator.');
            }
            else
            {
                setErrorMessage(`Error purging job: ${error.message}`);
            }
        }
  
    }

    const refreshJobDetails = async() =>{
        refetch();
    }

    const isPurgeable = useMemo(() => {
        if(isAdmin)
        {
            if (!data) return false;
            return data.state?.data?.state === "Completed" || data.state?.data?.state === "Failed" || data.state?.data?.state === "Paused";
        }
        return false;
        
    }, [data]);

    const handleDownload = (downloadFileDetails) => {
        const progress = downloadFileDetails.progress;
        const downloadError = downloadFileDetails.error;
        const downloadFileId = downloadFileDetails.id;
        console.log(downloadFileDetails)
        if (progress === 100 || downloadError === true) {
            removeDownload(downloadFileId);
        }
        else
        {
            console.log(`Cancelling download for file ID: ${downloadFileId}`);
            cancelDownload(downloadFileId);
        }

    }
    return(
        <>
        {
            isLoading ? (
                <Container fluid className="loading-container">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status" style={{width: '3rem', height: '3rem'}}>
                            <span className="visually-hidden">Loading job details...</span>
                        </div>
                        <div className="mt-3">
                            <h5>Loading Job {id}</h5>
                            <p className="text-muted">Please wait while we fetch the job details...</p>
                        </div>
                    </div>
                </Container>
            ) :
            isError ? (
                <Container fluid className="loading-container">
                    <Alert variant="danger" className="text-center">
                        <ExclamationTriangleFill className="me-2"/>
                        {
                            error?.status === 403 ? 'You do not have permission to view this job. Please contact your administrator.' : 'An error occurred while fetching the job details.'
                        }
                        <Button variant="outline-danger" className="ms-3" onClick={refetch}>
                            <ArrowClockwise className="me-1"/>
                            Try Again
                        </Button>
                    </Alert>
                </Container>
            ):
            <>
                {
                    errorMessage && (
                    <Alert variant="danger" onClose={() => setErrorMessage(false)} dismissible>
                        {errorMessage}
                    </Alert>
                    )
                }
                <>
                    <h3 className="display-6 job-page-header" >Job {id}</h3>
                    <Container fluid className='p-1'>
                        <Row xs={12} sm={12} md={12} lg={12}>
                            <Col xs={{offset:10, span:2}} sm={{offset:10, span:2}} md={{offset:11, span:1}} lg={{offset:11, span:1}}>
                                <div className="right-button-container">
                                    <JobActions jobDetails={data} queryClient={queryClient} id={id} active={false}/>
                                    <Dropdown>
                                        <Dropdown.Toggle 
                                            id="job-action-dropdown-button" 
                                            className="button-table"
                                            aria-label="Job actions menu">
                                            <ThreeDotsVertical/>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu role="menu"
                                            id="job-actions-dropdown-menu">
                                            <Dropdown.Item id="purge-job-button" onClick={purgeJob} role="menuitem" disabled={!isPurgeable}>Purge Job</Dropdown.Item>
                                            <Dropdown.Item id="refresh-job-button" onClick={refreshJobDetails} role="menuitem" disabled={!isJobRunning}>Refresh Details</Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </Col>
                        </Row>
                    </Container>            
                    <JobAccessModal show={openAccess} setShow={setOpenAccess}/>
                    <Container fluid className="p-5 bg-light" id="job-information-container">
                        <Row>
                            <Col xs={12} md={6} lg={4}>
                            <h5>Job Information</h5>
                                <Table id="job-information-table" size="sm" bordered striped className='wrap-text-cell'>
                                    <tbody>
                                        <tr id="job-description">
                                            <td id="job-description-label"><strong>Description</strong></td>
                                            <td id="job-description-value">{data.description}</td>
                                        </tr>
                                        <tr id="job-bill-code">
                                        <td id="job-bill-code-label"><strong>Bill Code</strong></td>
                                        <td id="job-bill-code-value">{data.billCode}</td>
                                        </tr>
                                        <tr id="job-state">
                                            <td id="job-state-label"><strong>State</strong></td>
                                            {
                                                data.stateError ? (
                                                  <td id="job-state-value" className="text-danger">
                                                        <ExclamationCircle className="me-1" />{data.state}
                                                    </td>
                                                ) : data.stateMessage ? (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top" data-bs-theme="dark">{data.stateMessage}</Tooltip>}>
                                                        <td id="job-state-value">{data.state}</td>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <td id="job-state-value">{data.state}</td>
                                                )
                                            }
                                        </tr>
                                        <tr id="job-priority">
                                            <td id="job-priority-label"><strong>Priority</strong></td>
                                            <td id="job-priority-value">{data.priority}</td>
                                        </tr>
                                        <tr id="job-created">
                                            <td id="job-created-label"><strong>Created</strong></td>
                                            <td id="job-created-value">{dateTimeFormatter({ value: data?.created?.timestamp })}</td>
                                        </tr>
                                        <tr id="job-avg-task-duration">
                                            <td id="job-avg-task-duration-label"><strong>Average Task Duration</strong></td>
                                            <td id="job-avg-task-duration-value">{durationTaskFormatter(data?.taskSummary?.averageDuration, data.state)}</td>
                                        </tr>
                                        <tr id="job-total-duration">
                                            <td id="job-total-duration-label"><strong>Total Job Duration</strong></td>
                                            <td id="job-total-duration-value">{durationTaskFormatter(data?.taskSummary?.totalDuration, data?.state)}</td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <h5>Job Progress</h5>
                                <JobProgress jobData={data}/>
                            </Col>
                            <Col xs={12} md={6} lg={4}>
                                <Row>
                                    <Accordion className="bg-light">
                                        <Accordion.Item eventKey="0" className="bg-light">
                                            <Accordion.Header className="bg-light">Metadata</Accordion.Header>
                                            <Accordion.Body className="bg-light">
                                            <Table size="sm" bordered className='wrap-text-cell'>
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Job Application</strong></td>
                                                            <td>{data.metadata.G3MetaApplication}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Job Group</strong></td>
                                                            <td>{data.metadata.G3MetaGroupId === "" ? 'N/A' : data.metadata.G3MetaGroupId}</td>
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                        <Accordion.Item eventKey="2" className="bg-light">
                                            <Accordion.Header className="bg-light">Entry Points</Accordion.Header>
                                            <Accordion.Body className="bg-light">
                                                <Table size="sm" bordered>
                                                    <tbody>
                                                        <tr>
                                                            <td><strong>Task Generation</strong></td>
                                                            {data.taskGenerationEntryPoint ? (
                                                            <td>{data.taskGenerationEntryPoint?.fileName}/{data.taskGenerationEntryPoint?.method}</td>
                                                            ) : (
                                                                <td className='text-muted'>Not available</td>
                                                            )}
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Task Execution</strong></td>
                                                            {data.taskExecutionEntryPoint ? (
                                                            <td>{data.taskExecutionEntryPoint?.fileName} / {data.taskExecutionEntryPoint?.method}</td>
                                                            ) : (
                                                                <td className='text-muted'>Not available</td>
                                                            )}
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Result Gathering</strong></td>
                                                            {data.resultGatheringEntryPoint ? (
                                                            <td>{data.resultGatheringEntryPoint?.fileName} / {data.resultGatheringEntryPoint?.method}</td>
                                                            ) : (
                                                                <td className='text-muted'>Not available</td>
                                                            )}
                                                        </tr>
                                                    </tbody>
                                                </Table>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    </Accordion>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                    <Container fluid className='job-tab-conatiner'>
                        <Tabs
                            id="job-data-tabs"
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="mb-3"
                            aria-label="Job data tabs">
                            <Tab eventKey={"tasks"} title="Tasks" id='job-tasks' aria-controls='job-tasks-content'>
                                <Container fluid className='job-tasks-tab-container'>
                                    <JobTasks jobId={id} active = {activeTab === 'tasks'} isJobRunning={isJobRunning} jobState={data.state}/>
                                </Container>
                            </Tab>
                            <Tab eventKey={"inputs"} title="Input Files" id='job-inputs-tab' aria-controls='job-inputs-content'>
                                <Container fluid className='job-files-tab-container'>
                                    {tabErrorMessage && tabErrorMessage['input'] && activeTab === 'inputs' && (
                                        <Alert variant="danger" onClose={() => setTabErrorMessage(prev => ({...prev, input: null}))}>
                                            {tabErrorMessage['input']}
                                        </Alert>
                                    )}
                                    <ModFileTable tableSize={'half-page-table'} fileTableId={"input-files"} getRows={getInputFiles} additionalColumns={[]} addButtonConfiguration= {{disableAdd: true, hideAdd: true}} 
                                    active={activeTab==='inputs'} isJobRunning={isJobRunning}/>
                                </Container>
                            </Tab>
                            <Tab eventKey={"outputs"} title="Output Files" id='job-outputs-tab' aria-controls='job-outputs-content'>
                                <Container fluid className='job-files-tab-container'>
                                    {tabErrorMessage && tabErrorMessage['output'] && activeTab === 'outputs' && (
                                        <Alert variant="danger" onClose={() => setTabErrorMessage(prev => ({...prev, output: null}))}>
                                            {tabErrorMessage['output']}
                                        </Alert>
                                    )}
                                    <ModFileTable tableSize={'half-page-table'} fileTableId={"output-files"} getRows={getOutputFiles} additionalColumns={[]} addButtonConfiguration={{disableAdd: true, hideAdd: true}} 
                                    active={activeTab==='outputs'} isJobRunning={isJobRunning}/>
                                </Container>
                            </Tab>
                            <Tab eventKey={"logs"} title="Log Files" id='job-logs-tab' aria-controls='job-logs-content'>
                                <Container fluid className='job-files-tab-container'>
                                    {tabErrorMessage && tabErrorMessage['log'] && activeTab === 'logs' && (
                                        <Alert variant="danger" onClose={() => setTabErrorMessage(prev => ({...prev, log: null}))}>
                                            {tabErrorMessage['log']}
                                        </Alert>
                                    )}
                                    <ModFileTable tableSize={'half-page-table'} fileTableId={"logs-files"} getRows={getLogFiles} additionalColumns={[]} addButtonConfiguration={{disableAdd: true, hideAdd: true}} 
                                    active={activeTab==='logs'} isJobRunning={isJobRunning}/>
                                </Container>
                            </Tab>
                            <Tab eventKey={"operation-summary"} title="Operation Summary" id='operation-history-tab' aria-controls='job-history-content'>
                                <Container fluid className='job-history-tab-container'>
                                    <Row>
                                        <JobHistory jobId={id} active={activeTab==="operation-summary"}  isJobRunning={isJobRunning}/>
                                    </Row>
                                </Container>
                            </Tab>
                        </Tabs>
                    </Container>
                    <ToastContainer className="toast-download-container">
                        <Toast show={downloads   !== undefined && downloads.length > 0} onClose={() => clearDownloads()}>
                            <Toast.Header closeButton={(downloads.length === completedDownloads.length)}>
                                <strong className="me-auto">Download Progress</strong>
                            </Toast.Header>
                            <Toast.Body className='download-toast-body'>
                                <Container>
                                {
                                    
                                    downloads && downloads.length > 0 && downloads.map((download, index) => (
                                        <Row className='download-row' key={index}>
                                            <Col xs={5} md={5} lg={5}>
                                                <div className="text-truncate">
                                                    {download.fileName}
                                                </div>
                                            </Col>
                                            <Col xs={7} md={7} lg={7}>
                                                <Row className="download-row g-0 flex-nowrap">
                                                    <Col xs={10} md={10} lg={10}>
                                                        <ProgressBar key={download.id} now={download.progress} animated={download.progress < 100} variant={download.error === true ? "danger" : undefined} label={`${Math.round(download.progress)}%`}/>
                                                    </Col>
                                                    <Col xs={2} md={2} lg={2} >
                                                        <Button className='button-table button-icon ms-2' size="sm" onClick={() => handleDownload(download)}><XCircle/></Button>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                ))}
                                </Container>
                            </Toast.Body>
                        </Toast>
                    </ToastContainer>
                </>
            </>
        }
        </>
        
    )
}